<script>
	import { onMount } from 'svelte';
	import Loader from './Loader.svelte';
	import TabView from './TabView.svelte';
	import TabPane from './TabPane.svelte';
	import WidgetSelect from './WidgetSelect.svelte';
	import { buildQuery, gameUrl, getApiUrl, getStatValue, injectGlobalStyles, loadJson, normalizeText, teamUrl } from './shared.js';

	export let personId = '';
	export let teamId = '';
	export let tag = 'mlbl';
	export let season = '';
	export let lang = 'ru';

	let host;
	let loading = false;
	let careerLoading = false;
	let statsLoading = false;
	let errorMessage = '';
	let coach = null;
	let careerItems = [];
	let stats = [];
	let selectedSeason = '';
	let statsErrorMessage = '';

	$: normalCareerRows = careerItems.filter((item) => item?.class === 'Normal');
	$: averageCareerRow = careerItems.find((item) => item?.class === 'Avg') || null;
	$: summaryCareerRow = careerItems.find((item) => item?.class === 'Sum') || null;

	$: seasonOptions = Array.from(new Set(normalCareerRows.map((item) => item?.season?.id || item?.season?.name)))
		.filter(Boolean)
		.map((seasonId, index) => {
			const season = normalCareerRows.find((item) => (item?.season?.id || item?.season?.name) === seasonId);
			return {
				value: String(seasonId),
				label: normalizeText(season?.season?.name || season?.season?.fullName || seasonId, `Сезон ${index + 1}`)
			};
		});

	$: normalStatsRows = stats.filter((item) => item?.class === 'Normal');
	$: sumStatsRow = stats.find((item) => item?.class === 'Sum') || null;
	$: avgStatsRow = stats.find((item) => item?.class === 'Avg') || null;

	function getCoachTitle(data) {
		return data?.fullName || [data?.firstName, data?.lastName].filter(Boolean).join(' ') || 'Тренер';
	}

	function getPositionTitle(data) {
		return data?.position?.name || data?.position || '—';
	}

	function getInitials(data) {
		const first = data?.firstName?.[0] || '';
		const last = data?.lastName?.[0] || '';
		return `${first}${last}`.trim() || 'C';
	}

	function formatStartsCheck(value) {
		if (value === 'true' || value === true || value === 1 || value === '1') {
			return '✓';
		}
		return '';
	}

	function formatGameResult(wins, loses) {
		if (wins === 1 || wins === '1' || wins === true) {
			return '✓';
		}
		if (loses === 1 || loses === '1' || loses === true) {
			return '✗';
		}
		return '';
	}

	function formatWinCell(wins, loses) {
		if (wins === 1 || wins === '1' || wins === true) {
			return '✓';
		}
		return '';
	}

	function formatLoseCell(wins, loses) {
		if (loses === 1 || loses === '1' || loses === true) {
			return '✓';
		}
		return '';
	}

	function getTeamId(team) {
		return team?.id || team?.teamId || '';
	}

	async function loadCoach() {
		if (!personId) {
			errorMessage = 'Не указан идентификатор тренера';
			coach = null;
			careerItems = [];
			return;
		}

		loading = true;
		careerLoading = true;
		errorMessage = '';
		try {
			const query = buildQuery({ teamId, tag, season, lang });
			const [coachInfo, career] = await Promise.all([
				loadJson(`${getApiUrl()}/ib/coach/${encodeURIComponent(personId)}/info${query}`),
				loadJson(`${getApiUrl()}/ib/coach/${encodeURIComponent(personId)}/career${query}`)
			]);

			coach = coachInfo;
			careerItems = Array.isArray(career) ? career : [];
			selectedSeason = computeFirstSeason(careerItems);
		} catch (error) {
			console.error('Load coach info error:', error);
			errorMessage = 'Ошибка загрузки тренера';
			coach = null;
			careerItems = [];
		} finally {
			loading = false;
			careerLoading = false;
		}
	}

	function computeFirstSeason(items) {
		for (const item of items || []) {
			if (item?.class !== 'Normal') continue;
			const seasonId = item?.season?.id || item?.season?.name;
			if (seasonId) return String(seasonId);
		}
		return '';
	}

	async function loadStats() {
		if (!personId || !selectedSeason) {
			stats = [];
			return;
		}

		statsLoading = true;
		statsErrorMessage = '';
		try {
			const query = buildQuery({ tag, season: selectedSeason, lang });
			stats = await loadJson(`${getApiUrl()}/ib/coach/${encodeURIComponent(personId)}/stats${query}`);
			if (!Array.isArray(stats)) {
				stats = [];
			}
		} catch (error) {
			console.error('Load coach stats error:', error);
			statsErrorMessage = 'Ошибка загрузки статистики';
			stats = [];
		} finally {
			statsLoading = false;
		}
	}

	$: if (seasonOptions.length > 0 && !seasonOptions.some((option) => option.value === selectedSeason)) {
		selectedSeason = seasonOptions[0].value;
	}

	$: if (selectedSeason && coach) {
		loadStats();
	}

	onMount(() => {
		const root = host?.getRootNode();
		if (root instanceof ShadowRoot) {
			injectGlobalStyles(root);
		}

		loadCoach();
	});
</script>

<div class="widget-card" bind:this={host}>
	{#if loading}
		<div class="widget-card__status"><Loader size="64px" /></div>
	{:else if errorMessage}
		<div class="widget-card__status">{errorMessage}</div>
	{:else if coach}
		<div class="profile">
			<div class="profile__head">
				<div class="profile__media">
					{#if coach.photo}
						<img class="profile__image" src={coach.photo} alt={getCoachTitle(coach)}>
					{:else}
						<div class="profile__placeholder">{getInitials(coach)}</div>
					{/if}
				</div>

				<div class="profile__title">
					<h3>{getCoachTitle(coach)}</h3>
					<p>{getPositionTitle(coach)}</p>
				</div>
			</div>

			<div class="profile__metrics">
				<div class="profile__metric">
					<span>Возраст</span>
					<strong>{normalizeText(coach.age)}</strong>
				</div>
				<div class="profile__metric">
					<span>Дата рождения</span>
					<strong>{normalizeText(coach.birthday)}</strong>
				</div>
				<div class="profile__metric">
					<span>Команд</span>
					<strong>{Array.isArray(coach.teams) ? coach.teams.length : 0}</strong>
				</div>
			</div>

			<TabView>

				<TabPane title="Карьера">
					<div class="widget-tabs__body">
						{#if careerLoading}
							<div class="widget-card__status"><Loader size="64px" /></div>
						{:else if !careerItems.length}
							<div class="widget-card__status">Данные о карьере не найдены</div>
						{:else}
							<div class="stats_table-wrap">
								<table class="stats_table career-table">
									<thead>
										<tr>
											<th>Сезон</th>
											<th>Команда</th>
											<th>Игры</th>
											<th>Победы</th>
											<th>Поражения</th>
											<th>Ф</th>
											<th>ФД</th>
											<th>ФС</th>
											<th>ФБ</th>
										</tr>
									</thead>
									<tbody>
										{#each normalCareerRows as item}
											<tr>
												<td>{normalizeText(item?.season?.name)}</td>
												<td>
													{#if getTeamId(item?.team)}
														<a href={teamUrl(getTeamId(item.team), { tag, season: item?.season?.id || item?.season?.name })} class="career-table__team-link">
															{normalizeText(item?.team?.shortName || item?.team?.name || item?.team?.label)}
														</a>
													{:else}
														{normalizeText(item?.team?.shortName || item?.team?.name || item?.team?.label)}
													{/if}
												</td>
												<td>{normalizeText(item?.games)}</td>
												<td>{getStatValue(item, 'wins')}</td>
												<td>{getStatValue(item, 'loses')}</td>
												<td>{getStatValue(item, 'foul')}</td>
												<td>{getStatValue(item, 'foulD')}</td>
												<td>{getStatValue(item, 'foulC')}</td>
												<td>{getStatValue(item, 'foulB')}</td>
											</tr>
										{/each}
										{#if summaryCareerRow}
										<tr class="career-table__summary-row">
											<td colspan="2">Всего за карьеру</td>
											<td>{normalizeText(summaryCareerRow?.games)}</td>
											<td>{getStatValue(summaryCareerRow, 'wins')}</td>
											<td>{getStatValue(summaryCareerRow, 'loses')}</td>
											<td>{getStatValue(summaryCareerRow, 'foul')}</td>
											<td>{getStatValue(summaryCareerRow, 'foulD')}</td>
											<td>{getStatValue(summaryCareerRow, 'foulC')}</td>
											<td>{getStatValue(summaryCareerRow, 'foulB')}</td>
										</tr>
										{/if}
										{#if averageCareerRow}
										<tr class="career-table__summary-row">
												<td colspan="3">В среднем</td>
												<td>{getStatValue(averageCareerRow, 'wins')}</td>
												<td>{getStatValue(averageCareerRow, 'loses')}</td>
												<td>{getStatValue(averageCareerRow, 'foul')}</td>
												<td>{getStatValue(averageCareerRow, 'foulD')}</td>
												<td>{getStatValue(averageCareerRow, 'foulC')}</td>
												<td>{getStatValue(averageCareerRow, 'foulB')}</td>
											</tr>
										{/if}
									</tbody>
								</table>
							</div>
						{/if}
					</div>
				</TabPane>

				<TabPane title="Статистика">
					<div class="widget-tabs__body">
						<div class="stats-toolbar">
							<WidgetSelect
								className="--black"
								name="coach-stats-season"
								options={seasonOptions}
								bind:value={selectedSeason}
								disabled={statsLoading || !seasonOptions.length}
							/>
						</div>

						{#if statsLoading}
							<div class="widget-card__status"><Loader size="64px" /></div>
						{:else if statsErrorMessage}
							<div class="widget-card__status">{statsErrorMessage}</div>
						{:else if !normalStatsRows.length}
							<div class="widget-card__status">Статистика не найдена</div>
						{:else}
							<div class="stats_table-wrap">
								<table class="stats_table coach-stats-table">
									<thead>
										<tr>
											<th>Дата</th>
											<th>Команда А</th>
											<th>Команда Б</th>
											<th>Счет</th>
											<th>Игры</th>
											<th>Победа</th>
											<th>Поражение</th>
											<th>Ф</th>
											<th>ФД</th>
											<th>ФС</th>
											<th>ФБ</th>
										</tr>
									</thead>
									<tbody>
										{#each normalStatsRows as item}
											<tr>
												<td>{normalizeText(item?.game?.gameDate)}</td>
												<td>
													{#if item?.game?.team1Id}
														<a href={teamUrl(item.game.team1Id, { tag })} class="career-table__team-link">
															{normalizeText(item?.game?.team1Name || item?.team?.name || item?.team?.label)}
														</a>
													{:else}
														{normalizeText(item?.game?.team1Name || item?.team?.name || item?.team?.label)}
													{/if}
												</td>
												<td>
													{#if item?.game?.team2Id}
														<a href={teamUrl(item.game.team2Id, { tag })} class="career-table__team-link">
															{normalizeText(item?.game?.team2Name)}
														</a>
													{:else}
														{normalizeText(item?.game?.team2Name)}
													{/if}
												</td>
												<td>
													{#if item?.game?.id}
														<a href={gameUrl(item.game.id, { tag, teamId })} style="cursor: pointer; color: #0066cc;">
															{normalizeText(item?.game?.score)}
														</a>
													{:else}
														{normalizeText(item?.game?.score)}
													{/if}
												</td>
												<td>{getStatValue(item, 'games')}</td>
												<td>{formatWinCell(item?.wins, item?.loses)}</td>
												<td>{formatLoseCell(item?.wins, item?.loses)}</td>
												<td>{getStatValue(item, 'foul')}</td>
												<td>{getStatValue(item, 'foulD')}</td>
												<td>{getStatValue(item, 'foulC')}</td>
												<td>{getStatValue(item, 'foulB')}</td>
											</tr>
										{/each}
										{#if sumStatsRow}
											<tr class="career-table__summary-row">
												<td colspan="4">ИТОГО:</td>
												<td>{getStatValue(sumStatsRow, 'games')}</td>
												<td>{getStatValue(sumStatsRow, 'wins')}</td>
												<td>{getStatValue(sumStatsRow, 'loses')}</td>
												<td>{getStatValue(sumStatsRow, 'foul')}</td>
												<td>{getStatValue(sumStatsRow, 'foulD')}</td>
												<td>{getStatValue(sumStatsRow, 'foulC')}</td>
												<td>{getStatValue(sumStatsRow, 'foulB')}</td>
											</tr>
										{/if}
										{#if avgStatsRow}
											<tr class="career-table__summary-row">
												<td colspan="4">В среднем:</td>
												<td>{getStatValue(avgStatsRow, 'games')}</td>
												<td>{getStatValue(avgStatsRow, 'wins')}</td>
												<td>{getStatValue(avgStatsRow, 'loses')}</td>
												<td>{getStatValue(avgStatsRow, 'foul')}</td>
												<td>{getStatValue(avgStatsRow, 'foulD')}</td>
												<td>{getStatValue(avgStatsRow, 'foulC')}</td>
												<td>{getStatValue(avgStatsRow, 'foulB')}</td>
											</tr>
										{/if}
									</tbody>
								</table>
							</div>
						{/if}
					</div>
				</TabPane>
			</TabView>
		</div>
	{/if}
</div>

<style>
	:host { display: block; }
	.widget-card {
		border: 1px solid #d7dce2;
		border-radius: 16px;
		padding: 18px;
		background: #fff;
		box-shadow: 0 6px 24px rgba(8, 22, 47, 0.05);
	}
	.widget-card__status {
		display: flex;
		justify-content: center;
		align-items: center;
		min-height: 160px;
		color: #53657b;
		text-align: center;
	}
	.profile__head {
		display: flex;
		gap: 16px;
		align-items: center;
	}
	.profile__media { flex: 0 0 auto; }
	.profile__image,
	.profile__placeholder {
		width: 88px;
		height: 88px;
		border-radius: 18px;
		object-fit: cover;
		background: #edf2f7;
	}
	.profile__placeholder {
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 28px;
		font-weight: 700;
		color: #00244d;
	}
	.profile__title h3 {
		margin: 0;
		font-size: 22px;
		color: #122033;
	}
	.profile__title p {
		margin: 6px 0 0;
		color: #53657b;
	}
	.profile__metrics {
		display: grid;
		grid-template-columns: repeat(3, minmax(0, 1fr));
		gap: 12px;
		margin: 18px 0 8px;
	}
	.profile__metric {
		padding: 12px 14px;
		border-radius: 12px;
		background: #f7f9fc;
	}
	.profile__metric span {
		display: block;
		margin-bottom: 6px;
		font-size: 12px;
		text-transform: uppercase;
		color: #6b7a90;
	}
	.profile__metric strong {
		font-size: 16px;
		color: #122033;
	}
	.widget-tabs__body {
		padding: 4px 0 0;
	}
	.widget-stats {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: 12px;
		margin: 0;
	}
	.widget-stats div {
		padding: 12px 14px;
		border-radius: 12px;
		background: #f7f9fc;
	}
	.widget-stats dt {
		margin-bottom: 6px;
		font-size: 12px;
		text-transform: uppercase;
		color: #6b7a90;
	}
	.widget-stats dd {
		margin: 0;
		color: #122033;
		font-weight: 600;
	}
	.career-table {
		min-width: 760px;
	}
	.career-table__summary-row td {
		font-weight: 700;
		background: #f7f9fc;
	}
	.career-table__team-link {
		color: #0066cc;
		text-decoration: none;
	}
	.career-table__team-link:hover {
		text-decoration: underline;
	}
	.stats-toolbar {
		display: flex;
		justify-content: flex-start;
		margin-bottom: 16px;
	}
	.stats-toolbar :global(.select-wrap) {
		width: 100%;
		max-width: 250px;
	}
	.coach-stats-table {
		min-width: 900px;
	}
	@media (max-width: 640px) {
		.profile__head { align-items: flex-start; }
		.profile__metrics,
		.widget-stats { grid-template-columns: 1fr; }
	}
</style>
